import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, Phone, Mail, MessageCircle } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { PRICING_TIERS, CONTACT } from "../data/services";

export default function PricingPage() {
  return (
    <div className="min-h-screen bg-[#F8F8F6] text-[#0E0E1A]">
      <Navbar />
      <section className="pt-36 pb-16 relative overflow-hidden">
        <div className="absolute -top-40 left-1/4 w-[500px] h-[500px] bg-cyan-500/15 blur-[140px] rounded-full" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 text-center">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00D4FF] mb-3">/ engagement tiers</p>
          <h1 className="font-display font-black text-5xl lg:text-7xl tracking-tight">
            Built around <span className="text-gradient-cyan">your scope</span>.
          </h1>
          <p className="text-[#5A5A72] mt-6 max-w-2xl mx-auto">
            Pick the tier that fits your ambition. Every project is priced custom — talk to Vedant for a no-pressure quote.
          </p>
        </div>
      </section>

      <section className="pb-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid md:grid-cols-3 gap-5">
            {PRICING_TIERS.map((t, i) => (
              <motion.div
                key={t.name} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className={`glass-card rounded-2xl p-8 relative ${t.highlight ? "border-[#00D4FF]/40 glow-cyan" : ""}`}
              >
                {t.highlight && <span className="absolute top-5 right-5 text-[10px] font-mono uppercase tracking-[0.2em] text-[#00D4FF] bg-[#00D4FF]/10 px-2.5 py-1 rounded-full">Most popular</span>}
                <h3 className="font-display font-bold text-3xl">{t.name}</h3>
                <p className="text-sm text-[#5A5A72] mt-1">{t.tagline}</p>
                <div className="mt-6">
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#00D4FF]">Pricing</p>
                  <p className="font-display font-black text-2xl mt-1 text-gradient-cyan">Talk to us</p>
                </div>
                <ul className="mt-7 space-y-3">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[#4B5563]">
                      <Check className="w-4 h-4 mt-0.5 text-[#00D4FF] flex-shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                <Link to="/quote" data-testid={`pricing-page-cta-${t.name.toLowerCase()}`} className={`mt-8 inline-flex w-full justify-center items-center gap-2 px-5 py-3.5 rounded-lg text-sm font-semibold ${t.highlight ? "btn-primary" : "btn-ghost"}`}>
                  Request quote <ArrowUpRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact for pricing */}
      <section className="pb-24">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="glass-card rounded-3xl p-8 lg:p-12 relative overflow-hidden"
          >
            <div className="absolute -top-20 -right-20 w-72 h-72 bg-cyan-500/15 blur-[100px] rounded-full" />
            <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-violet-500/15 blur-[100px] rounded-full" />
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00D4FF] mb-3">/ pricing discussion</p>
                <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tight">
                  For pricing,<br /><span className="text-gradient-cyan">talk to Vedant</span>.
                </h2>
                <p className="text-[#4B5563] mt-5 leading-relaxed">
                  Every Xion project is priced after a real conversation about scope, timeline, and outcome.
                  Reach out directly — usually replies the same day.
                </p>
              </div>
              <div className="space-y-3">
                <a
                  href={`tel:+91${CONTACT.phone}`}
                  data-testid="contact-phone-card"
                  className="group flex items-center gap-4 p-5 rounded-2xl bg-white border border-black/[0.08] hover:border-[#00D4FF]/50 hover:bg-[#00D4FF]/5 transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-cyan-400/20 to-teal-400/20 grid place-items-center border border-[#00D4FF]/30">
                    <Phone className="w-5 h-5 text-[#00D4FF]" />
                  </div>
                  <div className="flex-1">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#00D4FF]">Call / WhatsApp</p>
                    <p className="font-display font-bold text-xl mt-0.5 group-hover:text-gradient-cyan">{CONTACT.phoneDisplay}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-[#6B7280] group-hover:text-[#00D4FF] transition" />
                </a>
                <a
                  href={`mailto:${CONTACT.email}?subject=Xion%20Pricing%20Enquiry`}
                  data-testid="contact-email-card"
                  className="group flex items-center gap-4 p-5 rounded-2xl bg-white border border-black/[0.08] hover:border-[#00D4FF]/50 hover:bg-[#00D4FF]/5 transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-violet-400/20 to-indigo-400/20 grid place-items-center border border-violet-400/30">
                    <Mail className="w-5 h-5 text-violet-300" />
                  </div>
                  <div className="flex-1">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-violet-300">Email</p>
                    <p className="font-display font-bold text-xl mt-0.5 break-all">{CONTACT.email}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-[#6B7280] group-hover:text-[#00D4FF] transition" />
                </a>
                <Link
                  to="/quote"
                  data-testid="contact-quote-card"
                  className="group flex items-center gap-4 p-5 rounded-2xl btn-primary"
                >
                  <div className="w-12 h-12 rounded-xl bg-black/20 grid place-items-center">
                    <MessageCircle className="w-5 h-5" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/70">Or</p>
                    <p className="font-display font-bold text-lg mt-0.5">Send a written brief</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
